// Logout-time push cleanup, decoupled from React context.
//
// The unregister call has to happen while the JWT is still present (the endpoint
// is authenticated) but AuthProvider sits ABOVE PushProvider in the tree, so it
// cannot read push context. Instead PushProvider publishes the active token
// here, and AuthProvider.logout drains it.
//
// Cleanup is strictly best-effort: sign-out must succeed even when the network,
// Firebase or the API is down.

import { clearRegistrationRecord, deleteFcmToken, unregisterTokenFromBackend } from "./pushNotifications";

let activeToken: string | null = null;

/** Publishes (or clears) the token this browser currently has registered. */
export function setActivePushToken(token: string | null): void {
  activeToken = token;
}

export function getActivePushToken(): string | null {
  return activeToken;
}

/** Unregisters the active token, then drops the local FCM token and session
 *  record. Never throws and never rejects — callers await it inline in logout. */
export async function teardownPushOnLogout(): Promise<void> {
  const token = activeToken;
  activeToken = null;

  try {
    // Order matters: the DELETE needs the bearer token, so it must complete
    // before clearSession() runs.
    if (token) await unregisterTokenFromBackend(token);
    await deleteFcmToken();
  } catch {
    // Swallowed by design — a failed cleanup leaves a stale row the API prunes
    // via the stalePushTokensDays retention window.
  } finally {
    clearRegistrationRecord();
  }
}
